import { hasScope } from '@mesa/core/auth/utils';
import { REPO_SEARCH_MIN_LENGTH } from '@mesa/core/lib/constants';
import type { RepoRecord } from '@mesa/core/repo/index';
import { BookBookmarkIcon, DotsThreeIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type ColumnDef, getCoreRowModel, useReactTable, type VisibilityState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { DataTableColumnHeader } from '@/components/data-table/column-header';
import { DataTable } from '@/components/data-table/data-table';
import { DataTableSelectionToolbar } from '@/components/data-table/selection-toolbar';
import { DataTableToolbar } from '@/components/data-table/toolbar';
import { DataTableViewOptions } from '@/components/data-table/view-options';
import { ErrorState } from '@/components/error-state';
import { GettingStartedGuide } from '@/components/getting-started-guide';
import { LabelEditor } from '@/components/labels/label-editor-popover';
import { LabelFilter } from '@/components/labels/label-filter';
import { BulkLabelsDialog, CreateRepoDialog, DeleteRepoDialog, RenameRepoDialog } from '@/components/repo-dialogs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { pushError } from '@/lib/faro';
import { orgBySlugQueryOptions } from '@/lib/org-query-options';
import { ensureOrPrefetchInfiniteQuery } from '@/lib/query-utils';
import {
  repoCountQueryOptions,
  repoListInfiniteQueryOptions,
  repoTagCatalogQueryOptions,
} from '@/lib/repo-query-options';
import { repoSnippetsQueryOptions } from '@/lib/snippets';
import { bulkDeleteReposServerFn, bulkUpdateRepoTagsServerFn } from '@/server/functions/repo';

const repoSearchSchema = z.object({
  tags: z.array(z.string()).catch([]).optional(),
  search: z.string().optional(),
  sort: z.enum(['name', 'createdAt']).catch('createdAt').optional(),
  order: z.enum(['asc', 'desc']).catch('desc').optional(),
});

const EMPTY_TAGS: string[] = [];
const COLUMN_VISIBILITY_KEY = 'mesa:repo-table-columns';

function loadColumnVisibility(): VisibilityState {
  try {
    const stored = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (stored) return JSON.parse(stored) as VisibilityState;
  } catch {
    // ignore
  }
  return { defaultBranch: false };
}

/** A repo row combines a domain repo record with its tags for display in the table. */
type RepoRow = RepoRecord & { tags: Record<string, string> };

const relativeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

function formatRelativeTime(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);
  const diff = Date.now() - d.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return relativeFormatter.format(-minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return relativeFormatter.format(-hours, 'hour');
  const days = Math.floor(hours / 24);
  if (days < 30) return relativeFormatter.format(-days, 'day');
  const months = Math.floor(days / 30);
  if (months < 12) return relativeFormatter.format(-months, 'month');
  return relativeFormatter.format(-Math.floor(months / 12), 'year');
}

function RepositoriesSkeleton() {
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <BookBookmarkIcon />
          Repositories
        </h1>
        <Skeleton className="h-9 w-44 rounded" />
      </div>
      <div className="border border-mesa-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]" />
              <TableHead>Name</TableHead>
              <TableHead>Labels</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-4 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-32 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-40 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-6 rounded" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/$orgSlug/repositories')({
  validateSearch: repoSearchSchema,
  ssr: 'data-only',
  loaderDeps: ({ search: { tags, search, sort, order } }) => ({ tags, search, sort, order }),
  loader: async ({ context: { queryClient, org }, deps }) => {
    const opts = {
      tags: deps.tags ?? [],
      // Same gate as the component, so the prefetch shares its query key.
      search: deps.search && deps.search.length >= REPO_SEARCH_MIN_LENGTH ? deps.search : undefined,
      sortBy: deps.sort ?? 'createdAt',
      sortOrder: deps.order ?? 'desc',
    };
    await Promise.all([
      ensureOrPrefetchInfiniteQuery(
        queryClient,
        ['repo-list-page', org.slug],
        repoListInfiniteQueryOptions(org.slug, opts)
      ),
      queryClient.ensureQueryData(repoSnippetsQueryOptions()),
    ]);
  },
  pendingComponent: RepositoriesSkeleton,
  errorComponent: ErrorState,
  component: RepositoriesPage,
});

function RepositoriesPage() {
  'use no memo';

  const navigate = useNavigate({ from: Route.fullPath });
  const { tags: tagsFromSearch, search: searchFromUrl, sort, order } = Route.useSearch();
  const tags = tagsFromSearch ?? EMPTY_TAGS;
  const sortBy = sort ?? 'createdAt';
  const sortOrder = order ?? 'desc';
  const { org: routeOrg } = Route.useRouteContext();
  const { orgSlug } = Route.useParams();
  const { data: queryOrg } = useSuspenseQuery(orgBySlugQueryOptions(orgSlug));
  const org = queryOrg ?? routeOrg;
  const canCreateRepo = hasScope(org.scopes, 'write');
  const canDeleteRepo = hasScope(org.scopes, 'write');
  const queryClient = useQueryClient();

  // Terms shorter than REPO_SEARCH_MIN_LENGTH are ignored server-side (they can't
  // use the trigram index), so don't refetch for them.
  const search = (searchFromUrl?.length ?? 0) >= REPO_SEARCH_MIN_LENGTH ? searchFromUrl : undefined;
  const queryOpts = { tags, search, sortBy, sortOrder };
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useInfiniteQuery({
    ...repoListInfiniteQueryOptions(org.slug, queryOpts),
    placeholderData: keepPreviousData,
  });
  const { data: snippets } = useSuspenseQuery(repoSnippetsQueryOptions());

  const repos = useMemo(() => data?.pages.flatMap((p) => p.repos) ?? [], [data?.pages]);
  const tagsByRepo = useMemo(() => {
    const merged: Record<string, Record<string, string>> = {};
    for (const page of data?.pages ?? []) {
      Object.assign(merged, page.tagsByRepo);
    }
    return merged;
  }, [data?.pages]);
  // The label catalog is fetched only once the Labels panel is first opened;
  // computing it eagerly for large orgs made every page view scan the whole
  // org's labels (and once pushed the response past Lambda's 6MB cap).
  const [labelsRequested, setLabelsRequested] = useState(false);
  const { data: tagCatalog, isPending: tagCatalogLoading } = useQuery({
    ...repoTagCatalogQueryOptions(orgSlug),
    enabled: labelsRequested,
  });
  // No previous-data placeholder: a count from the old filter must never
  // render next to the new filter's rows — the summary hides until the count
  // for the current filter resolves. Sort-only changes keep the same key, so
  // the count survives those without refetching.
  const { data: totalCount } = useQuery(repoCountQueryOptions(org.slug, { tags, search }));

  const repoRows: RepoRow[] = useMemo(() => {
    return repos.map((repo) => ({
      ...repo,
      tags: tagsByRepo[repo.id] ?? {},
    }));
  }, [repos, tagsByRepo]);

  // Seed individual repo caches for hover-preload of detail pages
  useEffect(() => {
    if (!data?.pages) return;
    for (const page of data.pages) {
      for (const repo of page.repos) {
        queryClient.setQueryData(['repo', org.slug, repo.name], {
          repo,
          tags: page.tagsByRepo[repo.id] ?? {},
        });
      }
    }
  }, [data?.pages, org.slug, queryClient]);

  const searchValue = searchFromUrl ?? '';
  const searchTooShort = searchValue.length > 0 && searchValue.length < REPO_SEARCH_MIN_LENGTH;

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // Reset selection when search, tags, or sort change
  const filterKey = `${searchFromUrl ?? ''}-${tags.join(',')}-${sortBy}-${sortOrder}`;
  const prevFilterKey = useRef(filterKey);
  if (prevFilterKey.current !== filterKey) {
    prevFilterKey.current = filterKey;
    setRowSelection({});
  }

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(loadColumnVisibility);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkMetadataDialogOpen, setIsBulkMetadataDialogOpen] = useState(false);

  // The bulk-labels dialog offers existing labels as suggestions, so opening it
  // needs the lazily fetched catalog too.
  useEffect(() => {
    if (isBulkMetadataDialogOpen) setLabelsRequested(true);
  }, [isBulkMetadataDialogOpen]);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const [selectedRepo, setSelectedRepo] = useState<RepoRow | null>(null);

  const selectedRepoIds = useMemo(() => Object.keys(rowSelection).filter((id) => rowSelection[id]), [rowSelection]);
  const selectedCount = selectedRepoIds.length;

  // ---- Mutations ----
  const invalidateRepos = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['repo-list-page', org.slug] }),
      queryClient.invalidateQueries({ queryKey: ['repo-count', org.slug] }),
      queryClient.invalidateQueries({ queryKey: ['repo-tag-catalog', org.slug] }),
    ]);
  }, [queryClient, org.slug]);

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      await bulkDeleteReposServerFn({ data: { orgSlug: org.slug, repoIds: selectedRepoIds } });
      setRowSelection({});
      setIsBulkDeleteDialogOpen(false);
      await invalidateRepos();
    } catch (err) {
      pushError(err);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleAddLabel = useCallback(
    async (repoId: string, key: string, value: string) => {
      await bulkUpdateRepoTagsServerFn({ data: { orgSlug: org.slug, repoIds: [repoId], action: 'set', key, value } });
      void invalidateRepos();
    },
    [org.slug, invalidateRepos]
  );

  const handleRemoveLabel = useCallback(
    async (repoId: string, key: string) => {
      await bulkUpdateRepoTagsServerFn({ data: { orgSlug: org.slug, repoIds: [repoId], action: 'remove', key } });
      void invalidateRepos();
    },
    [org.slug, invalidateRepos]
  );

  // ---- Dialog open helpers ----
  const handleOpenRename = useCallback((repo: RepoRow) => {
    setSelectedRepo(repo);
    setIsRenameDialogOpen(true);
  }, []);

  const handleOpenDelete = useCallback((repo: RepoRow) => {
    setSelectedRepo(repo);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleSort = useCallback(
    (columnId: string) => {
      navigate({
        search: (prev) => ({
          ...prev,
          sort: columnId as 'name' | 'createdAt',
          order: prev.sort === columnId && prev.order === 'asc' ? 'desc' : 'asc',
        }),
      });
    },
    [navigate]
  );

  // ---- Columns ----
  const columns = useMemo<ColumnDef<RepoRow>[]>(() => {
    const cols: ColumnDef<RepoRow>[] = [
      {
        id: 'select',
        size: 40,
        enableSorting: false,
        enableHiding: false,
        header: ({ table }) => {
          const allSelected = table.getIsAllPageRowsSelected();
          const someSelected = table.getIsSomePageRowsSelected();
          return (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onCheckedChange={(checked) => table.toggleAllPageRowsSelected(!!checked)}
              aria-label="Select all"
            />
          );
        },
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(checked) => row.toggleSelected(!!checked)}
            aria-label="Select row"
          />
        ),
      },
      {
        accessorKey: 'name',
        enableSorting: true,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title="Name"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <Link
            to="/$orgSlug/repositories/$name"
            params={{ orgSlug: org.slug, name: row.original.name }}
            preload="intent"
            className="font-medium hover:underline"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'metadata',
        enableSorting: false,
        enableHiding: false,
        header: 'Labels',
        cell: ({ row }) => {
          const repo = row.original;
          const entries = Object.entries(repo.tags);
          const visible = entries.slice(0, 2);
          const remaining = entries.length - 2;
          const currentLabels = entries.map(([key, value]) => ({ key, value }));

          return (
            <Popover onOpenChange={(open) => open && setLabelsRequested(true)}>
              <PopoverTrigger asChild>
                <button type="button" className="flex flex-wrap gap-1 items-center min-h-[24px] w-full text-left">
                  {entries.length === 0 ? (
                    <span className="text-xs text-mesa-muted opacity-0 group-hover/row:opacity-100 transition-opacity inline-flex items-center gap-1">
                      <PlusIcon className="size-2.5" />
                      Add label
                    </span>
                  ) : (
                    <>
                      {visible.map(([key, value]) => (
                        <Badge key={key} variant="secondary">
                          {key}: {value}
                        </Badge>
                      ))}
                      {remaining > 0 && (
                        <Badge variant="outline" className="text-mesa-muted">
                          +{remaining} more
                        </Badge>
                      )}
                    </>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[300px] p-3">
                <LabelEditor
                  labels={currentLabels}
                  facets={tagCatalog ?? {}}
                  onAdd={(key, value) => handleAddLabel(repo.id, key, value)}
                  onRemove={(key) => handleRemoveLabel(repo.id, key)}
                />
              </PopoverContent>
            </Popover>
          );
        },
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        enableSorting: true,
        enableGlobalFilter: false,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title="Created"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => {
          const date = row.original.createdAt;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm text-mesa-muted cursor-default">{formatRelativeTime(date)}</span>
              </TooltipTrigger>
              <TooltipContent>{date.toLocaleString()}</TooltipContent>
            </Tooltip>
          );
        },
      },
    ];

    // Hidden-by-default columns
    cols.push({
      id: 'defaultBranch',
      accessorFn: (row) => row.defaultBranch,
      enableHiding: true,
      enableSorting: false,
      enableGlobalFilter: false,
      header: 'Default Bookmark',
      cell: ({ row }) => <code className="text-xs text-mesa-muted font-mono">{row.original.defaultBranch}</code>,
    });

    // Actions column
    cols.push({
      id: 'actions',
      size: 50,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const repo = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <DotsThreeIcon className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canCreateRepo && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleOpenRename(repo)}>
                    <PencilSimpleIcon className="h-4 w-4 mr-2" />
                    Rename
                  </DropdownMenuItem>
                </>
              )}
              {canDeleteRepo && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-mesa-red focus:text-mesa-red"
                    onClick={() => handleOpenDelete(repo)}
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    });

    return cols;
  }, [
    org.slug,
    canCreateRepo,
    canDeleteRepo,
    handleOpenRename,
    handleOpenDelete,
    handleSort,
    sortBy,
    sortOrder,
    tagCatalog,
    handleAddLabel,
    handleRemoveLabel,
  ]);

  // ---- Table instance ----
  // oxlint-disable-next-line react/incompatible-library -- This component has an explicit React Compiler boundary above.
  const table = useReactTable({
    data: repoRows,
    columns,
    state: { rowSelection, columnVisibility },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(next);
      localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(next));
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
  });

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          search: value || undefined,
        }),
      });
    },
    [navigate]
  );

  const handleResetFilters = useCallback(() => {
    navigate({ search: { tags: [], search: undefined, sort: undefined, order: undefined } });
  }, [navigate]);

  const isFiltered = searchValue !== '' || tags.length > 0;
  const isEmpty = repoRows.length === 0 && !isFiltered && !isFetching;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <BookBookmarkIcon />
          Repositories
        </h1>
        {canCreateRepo && (
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <PlusIcon className="h-4 w-4" />
            Create Repository
          </Button>
        )}
      </div>

      {isEmpty ? (
        /* True empty state: no repos at all, no filters */
        <div className="border border-mesa-border">
          <div className="flex flex-col items-center justify-center py-12">
            <BookBookmarkIcon className="h-12 w-12 text-mesa-muted mb-4" />
            <h3 className="text-lg font-semibold mb-2">No repositories yet</h3>
            <p className="text-mesa-muted text-center mb-4">
              {canCreateRepo
                ? 'Create a repository to start storing your code.'
                : 'No repositories have been created yet.'}
            </p>
            {canCreateRepo && (
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <PlusIcon className="h-4 w-4" />
                Create Repository
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <DataTable
            table={table}
            isFetching={isFetching}
            isLoading={isLoading}
            noResultsMessage="No repositories match your filters."
            toolbar={
              selectedCount > 0 ? (
                <DataTableSelectionToolbar
                  selectedCount={selectedCount}
                  onClearSelection={() => setRowSelection({})}
                  itemLabel="repo"
                >
                  <Button variant="outline" size="sm" className="h-8" onClick={() => setIsBulkMetadataDialogOpen(true)}>
                    <PencilSimpleIcon className="mr-2 size-3.5" />
                    Labels
                  </Button>
                  {canDeleteRepo && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-mesa-red hover:text-mesa-red"
                      onClick={() => setIsBulkDeleteDialogOpen(true)}
                    >
                      <TrashIcon className="mr-2 size-3.5" />
                      Delete
                    </Button>
                  )}
                </DataTableSelectionToolbar>
              ) : (
                <DataTableToolbar
                  searchValue={searchValue}
                  onSearchChange={handleSearchChange}
                  searchPlaceholder="Filter repositories..."
                  searchHint={
                    searchTooShort ? `Type at least ${REPO_SEARCH_MIN_LENGTH} characters to filter` : undefined
                  }
                  isFiltered={isFiltered}
                  onResetFilters={handleResetFilters}
                  filters={
                    <LabelFilter
                      facets={tagCatalog ?? {}}
                      loading={tagCatalogLoading}
                      onOpenChange={(open) => {
                        if (open) setLabelsRequested(true);
                      }}
                      selectedFilters={tags}
                      onFilterChange={(filters) => {
                        navigate({ search: (prev) => ({ ...prev, tags: filters }) });
                      }}
                    />
                  }
                >
                  <DataTableViewOptions table={table} />
                </DataTableToolbar>
              )
            }
            pagination={
              // Paging depends only on the indexed page's hasNextPage — a
              // pending or failed count must not hide Load more.
              repoRows.length > 0 ? (
                <div className="flex flex-col items-center gap-2 py-4">
                  {totalCount !== undefined && (
                    <span className="text-sm text-mesa-muted">
                      Showing {repoRows.length} of {totalCount} repositories
                    </span>
                  )}
                  {hasNextPage && (
                    <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetching}>
                      {isFetching ? 'Loading...' : 'Load more'}
                    </Button>
                  )}
                </div>
              ) : null
            }
          />

          <GettingStartedGuide
            repos={repoRows}
            sdkSnippetHtml={snippets.sdkSnippetHtml}
            installSnippetHtml={snippets.installSnippetHtml}
          />
        </div>
      )}

      <CreateRepoDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        orgSlug={org.slug}
        onCreated={invalidateRepos}
      />
      <RenameRepoDialog
        open={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
        orgSlug={org.slug}
        repo={selectedRepo}
        onRenamed={invalidateRepos}
      />
      <DeleteRepoDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        orgSlug={org.slug}
        repo={selectedRepo}
        onDeleted={invalidateRepos}
      />
      <BulkLabelsDialog
        open={isBulkMetadataDialogOpen}
        onOpenChange={setIsBulkMetadataDialogOpen}
        orgSlug={org.slug}
        selectedRepoIds={selectedRepoIds}
        repos={repoRows}
        facets={tagCatalog ?? {}}
        onUpdated={invalidateRepos}
      />

      <AlertDialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedCount} {selectedCount === 1 ? 'repository' : 'repositories'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.{' '}
              {selectedCount === 1 ? 'This repository' : `These ${selectedCount} repositories`} and all{' '}
              {selectedCount === 1 ? 'its' : 'their'} data will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-mesa-red text-white hover:bg-mesa-red/90"
              disabled={isBulkDeleting}
            >
              {isBulkDeleting
                ? 'Deleting...'
                : `Delete ${selectedCount} ${selectedCount === 1 ? 'repository' : 'repositories'}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
